<template>
  <SubPageShell title="โปรไฟล์ของฉัน" nav="me" hide-header>
    <!-- Custom Profile Header -->
    <header class="cp-header">
      <div class="cp-top-bar">
        <h1 class="cp-title">โปรไฟล์</h1>
        <button class="cp-logout-btn" type="button" @click="onLogout" aria-label="ออกจากระบบ">
          <el-icon :size="20"><SwitchButton /></el-icon>
        </button>
      </div>

      <div class="cp-user-info">
        <div class="cp-avatar-wrap" :style="avatarStyle">
          <img v-if="liffProfile?.pictureUrl" :src="liffProfile.pictureUrl" class="cp-avatar-img" />
          <span v-else class="cp-avatar-initial">{{ initial }}</span>
          <div class="cp-verified-badge" title="ยืนยันตัวตนแล้ว">
            <el-icon :size="12"><CircleCheckFilled /></el-icon>
          </div>
        </div>
        <div class="cp-user-details">
          <h2 class="cp-name">{{ displayName }}</h2>
          <div class="cp-meta">
            <div class="cp-meta-item">
              <span class="cp-meta-label">เลขบัตรฯ</span>
              <span class="cp-meta-value mono">{{ idcardMasked }}</span>
            </div>
            <span class="cp-meta-sep">•</span>
            <div class="cp-meta-item">
              <span class="cp-meta-value">{{ ageText }}</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Phase 12.3 — health stats now live from BE. "—" shown when the
         field isn't filled yet; tap to open the edit dialog. -->
    <div class="p-stats">
      <div class="p-stat-card" @click="router.push('/customer/profile/health')">
        <el-icon :size="18" color="#3b82f6"><DataLine /></el-icon>
        <div class="p-stat-value">{{ weightText }}</div>
        <div class="p-stat-unit">กก.</div>
      </div>
      <div class="p-stat-card" @click="router.push('/customer/profile/health')">
        <el-icon :size="18" color="#3b82f6"><TopRight /></el-icon>
        <div class="p-stat-value">{{ heightText }}</div>
        <div class="p-stat-unit">ซม.</div>
      </div>
      <div class="p-stat-card" @click="router.push('/customer/profile/health')">
        <el-icon :size="18" color="#D83A5A"><CirclePlus /></el-icon>
        <div class="p-stat-value">{{ bloodText }}</div>
        <div class="p-stat-unit">กรุ๊ปเลือด</div>
      </div>
    </div>

    <!-- Current BMI (from self-reported weight/height) -->
    <button
      v-if="currentBmi"
      id="profile-bmi-strip"
      type="button"
      class="p-bmi"
      :class="'tone-' + currentBmi.tone"
      @click="router.push('/customer/profile/health')"
    >
      <div class="p-bmi-left">
        <span class="p-bmi-label">BMI ปัจจุบัน</span>
        <span class="p-bmi-value">{{ currentBmi.value.toFixed(1) }}</span>
        <span class="p-bmi-chip">{{ currentBmi.label }}</span>
      </div>
      <div class="p-bmi-right">
        <div class="p-bmi-gauge">
          <i class="seg s1" /><i class="seg s2" /><i class="seg s3" /><i class="seg s4" />
          <span class="p-bmi-dot" :style="{ left: bmiGaugePct(currentBmi.value) + '%' }" />
        </div>
        <span v-if="bodyUpdatedText" class="p-bmi-date">อัปเดต {{ bodyUpdatedText }}</span>
      </div>
    </button>

    <!-- Health Info Details -->
    <div class="p-section-head" style="margin-top: 16px;">
      <div class="p-section-title">ข้อมูลสุขภาพเพิ่มเติม</div>
      <button class="p-section-action" type="button" @click="router.push('/customer/profile/health')">
        <el-icon :size="12"><Edit /></el-icon> แก้ไข
      </button>
    </div>
    <div class="p-list">
      <div class="p-row" @click="router.push('/customer/profile/health')">
        <div class="p-row-icon blue">
          <el-icon :size="17"><Notebook /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">โรคประจำตัว</div>
          <div v-if="me?.diseaseinfo" class="p-row-value">{{ me.diseaseinfo }}</div>
          <div v-else class="p-row-value muted">ไม่มี หรือ ยังไม่ได้ระบุ</div>
        </div>
        <el-icon :size="14" color="#8B96B0"><ArrowRight /></el-icon>
      </div>
      <div class="p-row-divider" />
      <div class="p-row" @click="router.push('/customer/profile/health')">
        <div class="p-row-icon blue">
          <el-icon :size="17"><FirstAidKit /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">ประวัติแพ้ยา</div>
          <div v-if="me?.allergies" class="p-row-value">{{ me.allergies }}</div>
          <div v-else class="p-row-value muted">ไม่มี หรือ ยังไม่ได้ระบุ</div>
        </div>
        <el-icon :size="14" color="#8B96B0"><ArrowRight /></el-icon>
      </div>
    </div>

    <!-- Personal info (protected by PIN) -->
    <div class="p-section-head">
      <div class="p-section-title">ข้อมูลส่วนตัว</div>
      <button class="p-section-action" type="button" @click="openPersonalInfoPinModal">
        <el-icon :size="12"><Edit /></el-icon> แก้ไข
      </button>
    </div>
    <div class="p-list">
      <div class="p-row" @click="openPersonalInfoPinModal">
        <div class="p-row-icon blue">
          <el-icon :size="17"><User /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">ชื่อ-นามสกุล</div>
          <div class="p-row-value">{{ fullNameText }}</div>
        </div>
        <el-icon :size="14" color="#8B96B0"><ArrowRight /></el-icon>
      </div>
      <div class="p-row-divider" />
      <div class="p-row" @click="openPersonalInfoPinModal">
        <div class="p-row-icon blue">
          <el-icon :size="17"><UserFilled /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">เพศ</div>
          <div class="p-row-value">{{ genderText }}</div>
        </div>
        <el-icon :size="14" color="#8B96B0"><ArrowRight /></el-icon>
      </div>
      <div class="p-row-divider" />
      <div class="p-row" @click="openPersonalInfoPinModal">
        <div class="p-row-icon blue">
          <el-icon :size="17"><Calendar /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">วันเกิด</div>
          <div class="p-row-value">{{ birthdateText }}</div>
        </div>
        <el-icon :size="14" color="#8B96B0"><ArrowRight /></el-icon>
      </div>
      <div class="p-row-divider" />
      <div class="p-row" @click="openPersonalInfoPinModal">
        <div class="p-row-icon blue">
          <el-icon :size="17"><Postcard /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">เลขบัตรประชาชน</div>
          <div class="p-row-value mono">{{ idcardMasked }}</div>
        </div>
        <el-icon :size="14" color="#8B96B0"><ArrowRight /></el-icon>
      </div>

    </div>
    <div class="p-hint">
      <el-icon :size="12" color="#8B96B0"><Lock /></el-icon>
      <span>ยืนยัน PIN เพื่อเข้าแก้ไขข้อมูลส่วนตัว</span>
    </div>

    <!-- Contact info — Phase 12.2 wired to PATCH /public/patient/me -->
    <div class="p-section-head">
      <div class="p-section-title">ข้อมูลติดต่อ</div>
      <button class="p-section-action" type="button" @click="openContactDialog">
        <el-icon :size="12"><Edit /></el-icon> แก้ไข
      </button>
    </div>
    <div class="p-list">
      <div class="p-row" @click="onCopy(phoneText, 'เบอร์โทรศัพท์')">
        <div class="p-row-icon blue">
          <el-icon :size="17"><Phone /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">เบอร์โทรศัพท์</div>
          <div class="p-row-value">
            {{ phoneText }}
            <span class="p-pill-verified">
              <el-icon :size="9"><Check /></el-icon> ยืนยันแล้ว
            </span>
          </div>
        </div>
        <el-icon :size="14" color="#8B96B0"><DocumentCopy /></el-icon>
      </div>
      <div class="p-row-divider" />
      <div
        class="p-row"
        @click="emailText ? onCopy(emailText, 'อีเมล') : openContactDialog()"
      >
        <div class="p-row-icon blue">
          <el-icon :size="17"><Message /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">อีเมล</div>
          <div v-if="emailText" class="p-row-value">{{ emailText }}</div>
          <div v-else class="p-row-value muted">
            ยังไม่ระบุอีเมล — กด "แก้ไข"
          </div>
        </div>
        <el-icon :size="14" color="#8B96B0"><DocumentCopy /></el-icon>
      </div>
      <div class="p-row-divider" />
      <div class="p-row" @click="onCopy(addressText, 'ที่อยู่')">
        <div class="p-row-icon blue">
          <el-icon :size="17"><Location /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-label">ที่อยู่จัดส่ง</div>
          <div v-if="addressText" class="p-row-value multi">
            {{ addressText }}
          </div>
          <div v-else class="p-row-value muted">
            ยังไม่ระบุที่อยู่ — กด "แก้ไข"
          </div>
        </div>
        <el-icon :size="14" color="#8B96B0"><DocumentCopy /></el-icon>
      </div>
    </div>

    <!-- Phase 12.2 — Edit contact dialog (PATCH /public/patient/me) -->
    <el-dialog
      v-model="contactDialog"
      title="แก้ไขข้อมูลติดต่อ"
      width="92%"
      :close-on-click-modal="false"
      append-to-body
      class="p-edit-dialog"
    >
      <el-form
        ref="contactFormRef"
        :model="contactForm"
        :rules="contactRules"
        label-position="top"
        size="default"
      >
        <el-form-item label="เบอร์โทรศัพท์" prop="phone">
          <el-input
            v-model="contactForm.phone"
            placeholder="0812345678"
            maxlength="20"
          />
        </el-form-item>
        <el-form-item label="อีเมล" prop="email">
          <el-input
            v-model="contactForm.email"
            placeholder="name@example.com"
            maxlength="255"
          />
        </el-form-item>
        <el-form-item label="ที่อยู่จัดส่ง">
          <el-input
            v-model="contactForm.addressno"
            type="textarea"
            :rows="2"
            placeholder="เลขที่ ถนน ตำบล อำเภอ จังหวัด"
            maxlength="500"
          />
        </el-form-item>
        <el-form-item label="รหัสไปรษณีย์" prop="postalcode">
          <el-input
            v-model="contactForm.postalcode"
            placeholder="20230"
            maxlength="10"
          />
        </el-form-item>
        <el-form-item label="ที่อยู่จัดส่งยา (ถ้าต่างจากที่อยู่ด้านบน)">
          <el-input
            v-model="contactForm.sendaddress"
            type="textarea"
            :rows="2"
            placeholder="ว่างไว้ = ส่งตามที่อยู่หลัก"
            maxlength="500"
          />
        </el-form-item>
        <el-form-item label="อาชีพ">
          <el-input
            v-model="contactForm.jobname"
            placeholder="ตัวอย่าง: พนักงานบริษัท"
            maxlength="255"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="contactDialog = false">ยกเลิก</el-button>
        <el-button type="primary" :loading="saving" @click="saveContact">
บันทึก
</el-button>
      </template>
    </el-dialog>

    <!-- Personal Info PIN Verification Dialog -->
    <el-dialog
      v-model="personalInfoPinDialog"
      title="ยืนยันรหัส PIN"
      width="92%"
      :close-on-click-modal="false"
      append-to-body
      class="p-pin-dialog"
    >
      <div class="p-pin-dialog-body">
        <div class="p-pin-icon-wrap">
          <el-icon :size="28" color="#3b82f6"><Lock /></el-icon>
        </div>
        <p class="p-pin-hint">กรุณากรอก PIN 6 หลักเพื่อเข้าแก้ไขข้อมูลส่วนตัว</p>
        <PinPad
          ref="pinPadRef"
          v-model="enteredPin"
          :length="6"
          :disabled="pinVerifying"
          :hint="pinErrorMsg"
          @filled="onVerifyPinForPersonal"
        />
        <div v-if="pinVerifying" class="p-pin-verifying">กำลังตรวจสอบ PIN...</div>
      </div>
      <template #footer>
        <el-button @click="personalInfoPinDialog = false" :disabled="pinVerifying">ยกเลิก</el-button>
      </template>
    </el-dialog>

    <!-- Edit Personal Info Dialog -->
    <el-dialog
      v-model="personalInfoDialog"
      title="แก้ไขข้อมูลส่วนตัว"
      width="92%"
      :close-on-click-modal="false"
      append-to-body
      class="p-edit-dialog"
    >
      <div v-if="currentIdcardFormatted" class="p-readonly-box" style="margin-top: 0; margin-bottom: 16px;">
        <div class="p-readonly-row">
          <span class="p-readonly-label">เลขบัตรประชาชนปัจจุบัน</span>
          <span class="p-readonly-val mono">{{ currentIdcardFormatted }}</span>
        </div>
      </div>
      <el-form
        ref="personalInfoFormRef"
        :model="personalInfoForm"
        :rules="personalInfoRules"
        label-position="top"
        size="default"
      >
        <el-row :gutter="8">
          <el-col :xs="24" :sm="8">
            <el-form-item label="คำนำหน้า" prop="title">
              <el-select
                v-model="personalInfoForm.title"
                filterable
                allow-create
                default-first-option
                placeholder="เลือกหรือกรอก"
                style="width: 100%"
              >
                <el-option value="นาย" label="นาย" />
                <el-option value="นาง" label="นาง" />
                <el-option value="นางสาว" label="นางสาว" />
                <el-option value="ด.ช." label="ด.ช." />
                <el-option value="ด.ญ." label="ด.ญ." />
                <el-option value="เด็กชาย" label="เด็กชาย" />
                <el-option value="เด็กหญิง" label="เด็กหญิง" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="16">
            <el-form-item label="ชื่อ" prop="fname">
              <el-input
                v-model="personalInfoForm.fname"
                placeholder="ชื่อจริง"
                maxlength="100"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="นามสกุล" prop="lname">
          <el-input
            v-model="personalInfoForm.lname"
            placeholder="นามสกุล"
            maxlength="100"
          />
        </el-form-item>
        <el-form-item label="เพศ" prop="gender">
          <el-radio-group v-model="personalInfoForm.gender">
            <el-radio-button value="M">ชาย</el-radio-button>
            <el-radio-button value="F">หญิง</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="วันเกิด (พ.ศ.)" prop="birthdate">
          <ThaiDatePicker v-model="personalInfoForm.birthdate" />
        </el-form-item>

        <el-form-item label="เลขบัตรประชาชน (13 หลัก)" prop="idcard">
          <el-input
            v-model="idcardDisplay"
            inputmode="numeric"
            placeholder="X-XXXX-XXXXX-XX-X"
            maxlength="17"
            clearable
          >
            <template #prefix>
              <el-icon><Postcard /></el-icon>
            </template>
          </el-input>
        </el-form-item>


      </el-form>
      <template #footer>
        <el-button @click="personalInfoDialog = false" :disabled="savingPersonal">ยกเลิก</el-button>
        <el-button type="primary" :loading="savingPersonal" @click="savePersonalInfo">บันทึก</el-button>
      </template>
    </el-dialog>

    <!-- Emergency contact — Phase 12.4 wired to mpatient.emergency* columns -->
    <div class="p-section-head">
      <div class="p-section-title">ผู้ติดต่อฉุกเฉิน</div>
    </div>
    <div class="p-card-block">
      <div class="p-emerg" @click="openEmergencyDialog">
        <div class="p-emerg-icon">
          <el-icon :size="22"><Service /></el-icon>
        </div>
        <div v-if="emergencyName" class="p-emerg-info">
          <div class="p-emerg-name">{{ emergencyName }}</div>
          <div class="p-emerg-rel">{{ emergencySummary }}</div>
        </div>
        <div v-else class="p-emerg-info">
          <div class="p-emerg-name muted">ยังไม่ได้เพิ่มผู้ติดต่อฉุกเฉิน</div>
          <div class="p-emerg-rel">
            กดเพื่อเพิ่ม — แจ้งให้แพทย์ทราบได้เร็วขึ้นในกรณีฉุกเฉิน
          </div>
        </div>
        <el-icon class="p-emerg-edit" :size="18" color="#3b82f6">
<Edit />
</el-icon>
      </div>
    </div>

    <!-- Phase 12.4 — Emergency contact dialog -->
    <el-dialog
      v-model="emergencyDialog"
      title="ผู้ติดต่อฉุกเฉิน"
      width="92%"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form
        ref="emergencyFormRef"
        :model="emergencyForm"
        :rules="emergencyRules"
        label-position="top"
        size="default"
      >
        <el-form-item label="ชื่อ-สกุล" prop="emergencyName">
          <el-input
            v-model="emergencyForm.emergencyName"
            placeholder="คุณสมหญิง บัวเจริญ"
            maxlength="120"
          />
        </el-form-item>
        <el-form-item label="ความสัมพันธ์">
          <el-input
            v-model="emergencyForm.emergencyRelation"
            placeholder="ลูกสาว / คู่สมรส / พี่ชาย"
            maxlength="40"
          />
        </el-form-item>
        <el-form-item label="เบอร์โทรศัพท์" prop="emergencyPhone">
          <el-input
            v-model="emergencyForm.emergencyPhone"
            placeholder="0898765432"
            maxlength="20"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="emergencyDialog = false">ยกเลิก</el-button>
        <el-button type="primary" :loading="saving" @click="saveEmergency">
บันทึก
</el-button>
      </template>
    </el-dialog>

    <!-- My Family -->
    <div class="p-section-head">
      <div class="p-section-title">ครอบครัวของฉัน</div>
    </div>
    <div class="p-list">
      <div class="p-row" @click="router.push('/customer/family')">
        <div class="p-row-icon blue">
          <el-icon :size="17"><User /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-title">จัดการครอบครัว</div>
          <div class="p-row-sub">เพิ่มสมาชิกครอบครัวเพื่อทำรายการแทน</div>
        </div>
        <el-icon :size="14" color="#8B96B0"><ArrowRight /></el-icon>
      </div>
    </div>

    <!-- Security — change-PIN + security-questions defer until BE endpoints
         ship; devices/login history are view-only audit info, no chevron. -->
    <div class="p-section-head">
      <div class="p-section-title">ความปลอดภัย</div>
    </div>
    <div class="p-list">
      <div class="p-row" @click="onSecurityDeferred">
        <div class="p-row-icon amber">
          <el-icon :size="17"><Key /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-title">เปลี่ยน PIN</div>
          <div class="p-row-sub">{{ pinSetAtText }}</div>
        </div>
        <el-icon :size="14" color="#8B96B0"><ArrowRight /></el-icon>
      </div>
      <div class="p-row-divider" />
      <div class="p-row readonly">
        <div class="p-row-icon amber">
          <el-icon :size="17"><Cellphone /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-title">อุปกรณ์ที่ใช้งาน</div>
          <div class="p-row-sub">{{ deviceText }}</div>
        </div>
      </div>
      <div class="p-row-divider" />
      <div class="p-row readonly">
        <div class="p-row-icon amber">
          <el-icon :size="17"><Timer /></el-icon>
        </div>
        <div class="p-row-content">
          <div class="p-row-title">ประวัติการเข้าใช้งาน</div>
          <div class="p-row-sub">{{ lastLoginText }}</div>
        </div>
      </div>
    </div>

    <!-- LINE connection -->
    <div class="p-section-head">
      <div class="p-section-title">การเชื่อมต่อ</div>
    </div>
    <div class="p-card-block">
      <div class="p-line">
        <div class="p-line-icon">
          <span class="p-line-logo">LINE</span>
          <span class="p-line-dot"><el-icon :size="9"><Check /></el-icon></span>
        </div>
        <div class="p-line-info">
          <div class="p-line-title">บัญชี LINE</div>
          <div class="p-line-sub">
            เชื่อมต่อแล้ว · {{ liffProfile?.displayName || "ผู้ใช้ LINE" }}
          </div>
          <div class="p-line-bonus">รับการแจ้งเตือนผ่าน LINE</div>
        </div>
        <button class="p-line-btn" type="button" @click="onLineDeferred">
          ยกเลิก
        </button>
      </div>
    </div>

    <!-- PDPA -->
    <div class="p-section-head">
      <div class="p-section-title">
        <el-icon :size="13" color="#1F7A4D"><CircleCheckFilled /></el-icon>
        ความเป็นส่วนตัว (PDPA)
      </div>
    </div>
    <div class="p-toggle-card">
      <div v-for="t in pdpaToggles" :key="t.key" class="p-toggle-row">
        <div class="p-toggle-info">
          <div class="p-toggle-title">{{ t.label }}</div>
          <div class="p-toggle-desc">{{ t.desc }}</div>
        </div>
        <button
          class="p-toggle"
          :class="{ on: t.value }"
          type="button"
          @click="togglePdpa(t)"
        >
          <span class="p-toggle-knob" />
        </button>
      </div>
    </div>

    <div class="p-action-grid">
      <button class="p-action-btn" type="button" @click="onPdpaExport">
        <el-icon :size="14" color="#3b82f6"><Download /></el-icon>
        <span>ดาวน์โหลดข้อมูล</span>
      </button>
      <button class="p-action-btn" type="button" @click="onPdpaConsentHistory">
        <el-icon :size="14" color="#3b82f6"><View /></el-icon>
        <span>ดูประวัติยินยอม</span>
      </button>
    </div>

    <!-- Phase 12.4 — Change PIN dialog -->
    <el-dialog
      v-model="pinDialog"
      title="เปลี่ยน PIN"
      width="92%"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form
        ref="pinFormRef"
        :model="pinForm"
        :rules="pinRules"
        label-position="top"
        size="default"
      >
        <el-form-item label="PIN ปัจจุบัน" prop="currentPin">
          <el-input
            v-model="pinForm.currentPin"
            type="password"
            show-password
            maxlength="6"
          />
        </el-form-item>
        <el-form-item label="PIN ใหม่ (6 หลัก)" prop="newPin">
          <el-input
            v-model="pinForm.newPin"
            type="password"
            show-password
            maxlength="6"
          />
        </el-form-item>
        <el-form-item label="ยืนยัน PIN ใหม่" prop="confirmPin">
          <el-input
            v-model="pinForm.confirmPin"
            type="password"
            show-password
            maxlength="6"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="pinDialog = false">ยกเลิก</el-button>
        <el-button type="primary" :loading="pinSaving" @click="savePin">
บันทึก
</el-button>
      </template>
    </el-dialog>

    <!-- Phase 12.4 — Consent history dialog -->
    <el-dialog
      v-model="consentDialog"
      title="ประวัติการให้ความยินยอม"
      width="92%"
      append-to-body
    >
      <div v-if="consentLoading" class="p-consent-loading">กำลังโหลด...</div>
      <div v-else-if="consentEntries.length === 0" class="p-consent-loading">
        ยังไม่มีประวัติการยินยอม
      </div>
      <div v-else class="p-consent-list">
        <div v-for="(c, i) in consentEntries" :key="i" class="p-consent-row">
          <div class="p-consent-meta">
            <div class="p-consent-hosp">โรงพยาบาล {{ c.hospId }}</div>
            <div v-if="c.signedAt" class="p-consent-date">
              {{ thaiShortDate(c.signedAt) }}
            </div>
          </div>
          <el-tag
            :type="c.status === 'APPROVED' ? 'success' : 'danger'"
            size="small"
          >
            {{ c.status === "APPROVED" ? "ยินยอมแล้ว" : "ถอนความยินยอม" }}
          </el-tag>
        </div>
      </div>
      <template #footer>
        <el-button @click="consentDialog = false">ปิด</el-button>
      </template>
    </el-dialog>

    <!-- Notifications -->
    <div class="p-section-head">
      <div class="p-section-title">การแจ้งเตือน</div>
    </div>
    <div class="p-toggle-card">
      <div
        v-for="n in notifToggles"
        :key="n.key"
        class="p-toggle-row with-icon"
      >
        <el-icon :size="18" :color="n.color">
<component :is="n.icon" />
</el-icon>
        <div class="p-toggle-info">
          <div class="p-toggle-title">{{ n.label }}</div>
        </div>
        <button
          class="p-toggle"
          :class="{ on: n.value }"
          type="button"
          @click="toggleNotif(n)"
        >
          <span class="p-toggle-knob" />
        </button>
      </div>
    </div>

    <button class="p-logout" type="button" @click="onLogout">
      <el-icon :size="16"><SwitchButton /></el-icon>
      ออกจากระบบ
    </button>

    <div class="p-footer">
      VBR Health link v2.5.1<br />
      © 2569 รพ.วิภาราม แหลมฉบัง
    </div>
  </SubPageShell>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, markRaw } from "vue";
import { useRouter } from "vue-router";
import {
  CircleCheckFilled,
  DataLine,
  TopRight,
  CirclePlus,
  Postcard,
  Calendar,
  Lock,
  Edit,
  Phone,
  Message,
  Location,
  Check,
  ArrowRight,
  DocumentCopy,
  Key,
  Cellphone,
  Timer,
  Service,
  Download,
  View,
  SwitchButton,
  FirstAidKit,
  Bell,
  Promotion,
  User,
  UserFilled,
  Notebook,
} from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import type { FormInstance, FormRules } from "element-plus";
import SubPageShell from "@/components/customer/SubPageShell.vue";
import PinPad from "@/components/customer/PinPad.vue";
import ThaiDatePicker from "@/components/customer/ThaiDatePicker.vue";
import { formatThaiId, formatThaiIdPartial, isValidThaiId } from "@/shared/idcard";
import { useLiff } from "@/composables/useLiff";
import { useCustomerMe, type CustomerMe } from "@/composables/useCustomerMe";
import { calcBmi, bmiGaugePct, formatThaiDate } from "@/utils/bmi";
import { setAccessToken } from "@/api/http";
import { publicApi } from "@/api/public.api";

const router = useRouter();
const { profile: liffProfile, init: initLiff } = useLiff();
const { me, load: loadMe, update: updateMe } = useCustomerMe();

const displayName = computed(() => {
  if (me.value?.fname || me.value?.lname) {
    return `คุณ${me.value.fname ?? ""} ${me.value.lname ?? ""}`.trim();
  }
  return liffProfile.value?.displayName ?? "ผู้ใช้";
});

const initial = computed(() => {
  const name = me.value?.fname ?? liffProfile.value?.displayName ?? "?";
  return [...name][0]?.toUpperCase() ?? "?";
});

const avatarStyle = computed(() => {
  const seed = liffProfile.value?.userId ?? "0";
  const hue = (seed.charCodeAt(2) ?? 200) % 360;
  return {
    background: `linear-gradient(135deg, hsl(${hue}, 70%, 65%), hsl(${(hue + 40) % 360}, 70%, 55%))`,
  };
});

const idcardMasked = computed(
  () => me.value?.idcardMasked ?? "1-XXXX-XXXXX-XX-8",
);
const phoneText = computed(() => me.value?.phone ?? "081-234-5678");
// Phase 12.4 — email is now persisted on mpatient. Empty string is the
// "not set" marker so the template can switch to the muted prompt.
const emailText = computed(() => me.value?.email ?? "");
const addressText = computed(() => {
  const parts: string[] = [];
  if (me.value?.addressno) parts.push(me.value.addressno);
  if (me.value?.sendaddress && me.value.sendaddress !== me.value.addressno) {
    parts.push(`จัดส่งยา: ${me.value.sendaddress}`);
  }
  if (me.value?.postalcode) parts.push(me.value.postalcode);
  return parts.join(" · ");
});

const ageText = computed(() =>
  me.value?.age != null ? `${me.value.age} ปี` : "—",
);
const weightText = computed(() =>
  me.value?.weight != null ? String(me.value.weight) : "—",
);
const heightText = computed(() =>
  me.value?.height != null ? String(me.value.height) : "—",
);
const bloodText = computed(() => me.value?.bloodGroup ?? "—");
const currentBmi = computed(() => calcBmi(me.value?.weight, me.value?.height));
const bodyUpdatedText = computed(() => formatThaiDate(me.value?.bodyMeasuredAt));

// Phase 12.4 — security panel live data. PIN last-change comes from
// patient.pinSetAt; device count + last login come from the loginAttempt
// roll-up the BE computes in getMe. Strings are humanised to Thai.
const TH_MONTHS_SHORT = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];
function thaiShortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${TH_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear() + 543}`;
}
function relativeDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (sameDay) return `วันนี้ ${hh}:${mm} น.`;
  if (isYesterday) return `เมื่อวาน ${hh}:${mm} น.`;
  return `${thaiShortDate(iso)} ${hh}:${mm} น.`;
}
const pinSetAtText = computed(() => {
  if (!me.value?.pinSetAt) return "ยังไม่ได้ตั้ง PIN";
  return `ตั้งล่าสุด ${thaiShortDate(me.value.pinSetAt)}`;
});
const deviceText = computed(() => {
  const n = me.value?.deviceCount ?? 0;
  if (n === 0) return "ยังไม่มีข้อมูล";
  return `${n} อุปกรณ์ใน 90 วันล่าสุด`;
});
const lastLoginText = computed(() => {
  if (!me.value?.lastLoginAt) return "ยังไม่มีข้อมูลการเข้าใช้";
  return `ล่าสุด: ${relativeDay(me.value.lastLoginAt)}`;
});

// Personal info display computed
const fullNameText = computed(() => {
  const parts = [me.value?.title, me.value?.fname, me.value?.lname].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return displayName.value;
});
const genderText = computed(() => {
  if (me.value?.gender === "M") return "ชาย";
  if (me.value?.gender === "F") return "หญิง";
  return "ยังไม่ได้ระบุ";
});
const birthdateText = computed(() => {
  if (!me.value?.birthdate) return "ยังไม่ได้ระบุ";
  const formatted = thaiShortDate(me.value.birthdate);
  return me.value.age != null ? `${formatted} (อายุ ${me.value.age} ปี)` : formatted;
});

// Personal Info PIN Verification and Edit Dialog state
const personalInfoPinDialog = ref(false);
const enteredPin = ref("");
const pinVerifying = ref(false);
const pinErrorMsg = ref("");
const pinPadRef = ref<{ shakeError: () => void } | null>(null);
const currentVerifiedIdcard = ref("");
const currentIdcardFormatted = computed(() => {
  const id = currentVerifiedIdcard.value || me.value?.idcard;
  if (id) return formatThaiId(id);
  return "";
});

function openPersonalInfoPinModal(): void {
  enteredPin.value = "";
  pinErrorMsg.value = "";
  personalInfoPinDialog.value = true;
}

async function onVerifyPinForPersonal(p: string): Promise<void> {
  pinVerifying.value = true;
  pinErrorMsg.value = "";
  try {
    const res = await publicApi.verifyProfilePin(p);
    let idcard = res?.idcard;
    if (!idcard) {
      const freshMe = await loadMe(true);
      idcard = freshMe?.idcard ?? undefined;
    }
    if (idcard && me.value) {
      (me.value as CustomerMe).idcard = idcard;
    }
    personalInfoPinDialog.value = false;
    openPersonalInfoDialog(idcard);
  } catch (err: unknown) {
    pinPadRef.value?.shakeError();
    const data = (err as { response?: { data?: { code?: string; message?: string } } }).response?.data;
    if (data?.code === "PIN_INCORRECT") {
      pinErrorMsg.value = "PIN ไม่ถูกต้อง กรุณาลองใหม่";
    } else if (data?.code === "PIN_LOCKED") {
      pinErrorMsg.value = data.message || "ระงับการใส่ PIN ชั่วคราว";
    } else if (data?.code === "PIN_NOT_SET") {
      pinErrorMsg.value = "ยังไม่ได้ตั้งค่า PIN";
    } else {
      pinErrorMsg.value = data?.message || "ตรวจสอบ PIN ไม่สำเร็จ";
    }
    setTimeout(() => {
      enteredPin.value = "";
    }, 400);
  } finally {
    pinVerifying.value = false;
  }
}

const personalInfoDialog = ref(false);
const savingPersonal = ref(false);
const personalInfoFormRef = ref<FormInstance | null>(null);
const personalInfoForm = reactive({
  idcard: "",
  title: "",
  fname: "",
  lname: "",
  birthdate: "",
  gender: "",
});

const idcardDisplay = computed<string>({
  get: () => formatThaiIdPartial(personalInfoForm.idcard),
  set: (val: string) => {
    personalInfoForm.idcard = (val || "").replace(/\D/g, "").slice(0, 13);
  },
});

const personalInfoRules: FormRules = {
  idcard: [
    { required: true, message: "กรุณากรอกเลขบัตรประชาชน", trigger: "blur" },
    {
      validator: (_r: unknown, _v: unknown, cb: (err?: Error) => void) => {
        const clean = (personalInfoForm.idcard || "").replace(/\D/g, "");
        if (clean.length !== 13) {
          cb(new Error("เลขบัตรประชาชนต้องมี 13 หลัก"));
        } else if (!isValidThaiId(clean)) {
          cb(new Error("เลขบัตรประชาชนไม่ถูกต้องตามหลักตรวจสอบ (Check Digit)"));
        } else {
          cb();
        }
      },
      trigger: "blur",
    },
  ],
  fname: [{ required: true, message: "กรุณากรอกชื่อ", trigger: "blur" }],
  lname: [{ required: true, message: "กรุณากรอกนามสกุล", trigger: "blur" }],
};

function openPersonalInfoDialog(verifiedIdcard?: string): void {
  const currentId = verifiedIdcard || me.value?.idcard || "";
  currentVerifiedIdcard.value = currentId;
  personalInfoForm.idcard = currentId;
  personalInfoForm.title = me.value?.title ?? "";
  personalInfoForm.fname = me.value?.fname ?? "";
  personalInfoForm.lname = me.value?.lname ?? "";
  personalInfoForm.birthdate = me.value?.birthdate ?? "";
  personalInfoForm.gender = me.value?.gender ?? "";
  personalInfoDialog.value = true;
}

async function savePersonalInfo(): Promise<void> {
  if (!personalInfoFormRef.value) return;
  try {
    await personalInfoFormRef.value.validate();
  } catch {
    return;
  }
  savingPersonal.value = true;
  try {
    const cleanId = personalInfoForm.idcard.replace(/\D/g, "");
    await updateMe({
      idcard: cleanId || undefined,
      title: personalInfoForm.title.trim(),
      fname: personalInfoForm.fname.trim(),
      lname: personalInfoForm.lname.trim(),
      fnameth: personalInfoForm.fname.trim(),
      lnameth: personalInfoForm.lname.trim(),
      birthdate: personalInfoForm.birthdate || undefined,
      gender: personalInfoForm.gender || undefined,
    });
    if (cleanId) {
      currentVerifiedIdcard.value = cleanId;
    }
    ElMessage.success("บันทึกข้อมูลส่วนตัวเรียบร้อย");
    personalInfoDialog.value = false;
  } catch (e) {
    const data = (e as { response?: { data?: { code?: string; message?: string } } }).response?.data;
    if (data?.code === "IDCARD_DUPLICATE") {
      ElMessage.error("เลขบัตรประชาชนนี้มีผู้ใช้งานในระบบแล้ว");
    } else {
      ElMessage.error(data?.message ?? "บันทึกข้อมูลไม่สำเร็จ");
    }
  } finally {
    savingPersonal.value = false;
  }
}

// Phase 12.2 — edit contact dialog (PATCH /public/patient/me)
const contactDialog = ref(false);
const saving = ref(false);
const contactFormRef = ref<FormInstance | null>(null);
const contactForm = reactive({
  phone: "",
  email: "",
  postalcode: "",
  addressno: "",
  sendaddress: "",
  jobname: "",
  diseaseinfo: "",
  allergies: "",
});
const contactRules: FormRules = {
  phone: [
    { required: true, message: "กรุณากรอกเบอร์โทรศัพท์", trigger: "blur" },
    {
      pattern: /^[0-9+\-\s()]{6,20}$/,
      message: "รูปแบบเบอร์ไม่ถูกต้อง",
      trigger: "blur",
    },
  ],
  email: [
    {
      pattern: /^$|^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "รูปแบบอีเมลไม่ถูกต้อง",
      trigger: "blur",
    },
  ],
  postalcode: [
    {
      pattern: /^[0-9]{0,10}$/,
      message: "รหัสไปรษณีย์ต้องเป็นตัวเลข",
      trigger: "blur",
    },
  ],
};

function openContactDialog(): void {
  contactForm.phone = me.value?.phone ?? "";
  contactForm.email = me.value?.email ?? "";
  contactForm.postalcode = me.value?.postalcode ?? "";
  contactForm.addressno = me.value?.addressno ?? "";
  contactForm.sendaddress = me.value?.sendaddress ?? "";
  contactForm.jobname = me.value?.jobname ?? "";
  contactDialog.value = true;
}

async function saveContact(): Promise<void> {
  if (!contactFormRef.value) return;
  try {
    await contactFormRef.value.validate();
  } catch {
    return;
  }
  saving.value = true;
  try {
    await updateMe({
      phone: contactForm.phone.trim(),
      email: contactForm.email.trim(),
      postalcode: contactForm.postalcode.trim(),
      addressno: contactForm.addressno.trim(),
      sendaddress: contactForm.sendaddress.trim(),
      jobname: contactForm.jobname.trim(),
    });
    ElMessage.success("บันทึกข้อมูลแล้ว");
    contactDialog.value = false;
  } catch (e) {
    const msg = (e as { response?: { data?: { message?: string } } }).response
      ?.data?.message;
    ElMessage.error(msg ?? "บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง");
  } finally {
    saving.value = false;
  }
}

// Phase 12.4 — emergency contact dialog + display computed.
const emergencyName = computed(() => me.value?.emergencyName ?? "");
const emergencySummary = computed(() => {
  const rel = me.value?.emergencyRelation;
  const phone = me.value?.emergencyPhone;
  return [rel, phone].filter(Boolean).join(" · ") || "—";
});
const emergencyDialog = ref(false);
const emergencyFormRef = ref<FormInstance | null>(null);
const emergencyForm = reactive({
  emergencyName: "",
  emergencyRelation: "",
  emergencyPhone: "",
});
const emergencyRules: FormRules = {
  emergencyPhone: [
    {
      pattern: /^$|^[0-9+\-\s()]{6,20}$/,
      message: "รูปแบบเบอร์ไม่ถูกต้อง",
      trigger: "blur",
    },
  ],
};

function openEmergencyDialog(): void {
  emergencyForm.emergencyName = me.value?.emergencyName ?? "";
  emergencyForm.emergencyRelation = me.value?.emergencyRelation ?? "";
  emergencyForm.emergencyPhone = me.value?.emergencyPhone ?? "";
  emergencyDialog.value = true;
}

async function saveEmergency(): Promise<void> {
  if (!emergencyFormRef.value) return;
  try {
    await emergencyFormRef.value.validate();
  } catch {
    return;
  }
  saving.value = true;
  try {
    await updateMe({
      emergencyName: emergencyForm.emergencyName.trim(),
      emergencyRelation: emergencyForm.emergencyRelation.trim(),
      emergencyPhone: emergencyForm.emergencyPhone.trim(),
    });
    ElMessage.success("บันทึกข้อมูลผู้ติดต่อฉุกเฉินแล้ว");
    emergencyDialog.value = false;
  } catch (e) {
    const msg = (e as { response?: { data?: { message?: string } } }).response
      ?.data?.message;
    ElMessage.error(msg ?? "บันทึกไม่สำเร็จ ลองใหม่อีกครั้ง");
  } finally {
    saving.value = false;
  }
}

// Phase 12.3 — toggles wired to /public/patient/preferences. Each switch
// PATCHes its specific field to BE on flip; failure rolls the local state
// back so the UI never lies about what's persisted.
type PdpaKey = "pdpaResearch" | "pdpaMarketing" | "pdpaPartners";
type NotifyKey =
  | "notifyAppointment"
  | "notifyPill"
  | "notifyVaccine"
  | "notifyPromo";

const pdpaToggles = reactive<
  Array<{ key: PdpaKey; label: string; desc: string; value: boolean }>
>([
  {
    key: "pdpaResearch",
    label: "การวิจัยทางการแพทย์",
    desc: "อนุญาตใช้ข้อมูลแบบไม่ระบุตัวตน",
    value: false,
  },
  {
    key: "pdpaMarketing",
    label: "การตลาดและโปรโมชั่น",
    desc: "รับข้อเสนอจากโรงพยาบาล",
    value: false,
  },
  {
    key: "pdpaPartners",
    label: "แชร์ข้อมูลกับ รพ.พันธมิตร",
    desc: "ส่งต่อรักษาในเครือข่าย",
    value: false,
  },
]);

const notifToggles = reactive<
  Array<{
    key: NotifyKey;
    label: string;
    icon: unknown;
    color: string;
    value: boolean;
  }>
>([
  {
    key: "notifyAppointment",
    label: "การนัดหมาย",
    icon: markRaw(Calendar),
    color: "#3b82f6",
    value: true,
  },
  {
    key: "notifyPill",
    label: "การกินยา",
    icon: markRaw(FirstAidKit),
    color: "#16a34a",
    value: true,
  },
  {
    key: "notifyVaccine",
    label: "วัคซีน",
    icon: markRaw(Bell),
    color: "#C97A0C",
    value: true,
  },
  {
    key: "notifyPromo",
    label: "โปรโมชั่น",
    icon: markRaw(Promotion),
    color: "#D83A5A",
    value: false,
  },
]);

async function togglePdpa(row: (typeof pdpaToggles)[number]): Promise<void> {
  const previous = row.value;
  row.value = !row.value;
  try {
    await publicApi.updatePreferences({ [row.key]: row.value });
  } catch {
    row.value = previous;
    ElMessage.error("บันทึกไม่สำเร็จ ลองใหม่");
  }
}

async function toggleNotif(row: (typeof notifToggles)[number]): Promise<void> {
  const previous = row.value;
  row.value = !row.value;
  try {
    await publicApi.updatePreferences({ [row.key]: row.value });
  } catch {
    row.value = previous;
    ElMessage.error("บันทึกไม่สำเร็จ ลองใหม่");
  }
}
// Phase 12.2 — copy plaintext to clipboard. Tap-to-copy is more useful
// than tap-to-edit on this page because the edit flow needs a BE PATCH
// endpoint that doesn't ship yet.
async function onCopy(text: string, label: string): Promise<void> {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success(`คัดลอก${label}แล้ว`);
  } catch {
    ElMessage.info(`${label}: ${text}`);
  }
}

// Phase 12.4 — all four deferred handlers are now wired to real endpoints.
// `onSecurityDeferred` opens the change-PIN dialog.
// `onLineDeferred` confirms then POSTs /auth/unlink-line.
// `onPdpaExport` downloads the PDPA blob as JSON.
// `onPdpaConsentHistory` loads + shows the consent timeline dialog.

// Change-PIN dialog state.
const pinDialog = ref(false);
const pinSaving = ref(false);
const pinFormRef = ref<FormInstance | null>(null);
const pinForm = reactive({ currentPin: "", newPin: "", confirmPin: "" });
const pinRules: FormRules = {
  currentPin: [
    { required: true, message: "กรุณากรอก PIN เดิม", trigger: "blur" },
    {
      pattern: /^\d{6}$/,
      message: "PIN ต้องเป็นตัวเลข 6 หลัก",
      trigger: "blur",
    },
  ],
  newPin: [
    { required: true, message: "กรุณากรอก PIN ใหม่", trigger: "blur" },
    {
      pattern: /^\d{6}$/,
      message: "PIN ต้องเป็นตัวเลข 6 หลัก",
      trigger: "blur",
    },
  ],
  confirmPin: [
    {
      validator: (_r, value: string, cb) => {
        if (value !== pinForm.newPin) cb(new Error("PIN ยืนยันไม่ตรงกัน"));
        else cb();
      },
      trigger: "blur",
    },
  ],
};
function onSecurityDeferred(): void {
  pinForm.currentPin = "";
  pinForm.newPin = "";
  pinForm.confirmPin = "";
  pinDialog.value = true;
}
async function savePin(): Promise<void> {
  if (!pinFormRef.value) return;
  try {
    await pinFormRef.value.validate();
  } catch {
    return;
  }
  pinSaving.value = true;
  try {
    await publicApi.changePin({
      currentPin: pinForm.currentPin,
      newPin: pinForm.newPin,
    });
    ElMessage.success("เปลี่ยน PIN เรียบร้อย");
    pinDialog.value = false;
    // Re-fetch me so the pinSetAt timestamp on the Security panel refreshes.
    await loadMe();
  } catch (e) {
    const data = (
      e as { response?: { data?: { code?: string; message?: string } } }
    ).response?.data;
    if (data?.code === "PIN_INCORRECT") {
      ElMessage.error("PIN เดิมไม่ถูกต้อง");
    } else if (data?.code === "PIN_UNCHANGED") {
      ElMessage.error("PIN ใหม่ต้องไม่เหมือนของเดิม");
    } else {
      ElMessage.error(data?.message ?? "เปลี่ยน PIN ไม่สำเร็จ");
    }
  } finally {
    pinSaving.value = false;
  }
}

async function onLineDeferred(): Promise<void> {
  try {
    await ElMessageBox.confirm(
      "ยกเลิกการเชื่อมต่อ LINE จะทำให้ออกจากระบบทันที — ต้องสแกน QR ใหม่จาก LINE OA เพื่อใช้งานครั้งหน้า",
      "ยกเลิกการเชื่อมต่อ LINE",
      {
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
        type: "warning",
      },
    );
  } catch {
    return;
  }
  try {
    await publicApi.unlinkLine();
    ElMessage.success("ยกเลิกการเชื่อมต่อแล้ว");
    setAccessToken(null, "customer");
    bounceToRegister();
  } catch (e) {
    const msg = (e as { response?: { data?: { message?: string } } }).response
      ?.data?.message;
    ElMessage.error(msg ?? "ยกเลิกไม่สำเร็จ");
  }
}

async function onPdpaExport(): Promise<void> {
  try {
    const data = await publicApi.pdpaExport();
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pdpa-export-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    ElMessage.success("ดาวน์โหลดข้อมูลส่วนตัวเรียบร้อย");
  } catch (e) {
    const msg = (e as { response?: { data?: { message?: string } } }).response
      ?.data?.message;
    ElMessage.error(msg ?? "ดาวน์โหลดไม่สำเร็จ");
  }
}

// Consent history dialog state.
const consentDialog = ref(false);
const consentLoading = ref(false);
const consentEntries = ref<
  Array<{
    hospId: string;
    status: "APPROVED" | "WITHDRAWN";
    signedAt: string | null;
  }>
>([]);
async function onPdpaConsentHistory(): Promise<void> {
  consentDialog.value = true;
  consentLoading.value = true;
  try {
    consentEntries.value = await publicApi.pdpaConsentHistory();
  } catch {
    ElMessage.error("โหลดประวัติยินยอมไม่สำเร็จ");
    consentEntries.value = [];
  } finally {
    consentLoading.value = false;
  }
}

function bounceToRegister(): void {
  const lastKey = sessionStorage.getItem("register:lastContractKey");
  if (lastKey)
    router.replace({
      name: "customer-register",
      params: { contractKey: lastKey },
    });
  else router.replace({ name: "not-found" });
}

async function onLogout(): Promise<void> {
  try {
    await ElMessageBox.confirm(
      "ออกจากระบบและกลับไปหน้าเข้าสู่ระบบ?",
      "ออกจากระบบ",
      {
        confirmButtonText: "ออกจากระบบ",
        cancelButtonText: "ยกเลิก",
        type: "warning",
      },
    );
    setAccessToken(null, "customer");
    bounceToRegister();
  } catch {
    /* user cancelled */
  }
}

onMounted(async () => {
  await initLiff();
  const result = await loadMe();
  if (!result) {
    bounceToRegister();
    return;
  }
  // Phase 12.3 — hydrate persisted PDPA + notification toggles. Non-fatal:
  // if the BE rejects, toggles fall back to their privacy-respecting
  // defaults (matches the server-side default for a missing row).
  try {
    const prefs = await publicApi.preferences();
    for (const t of pdpaToggles) t.value = prefs[t.key];
    for (const n of notifToggles) n.value = prefs[n.key];
  } catch {
    /* keep defaults */
  }
});
</script>

<style scoped>
/* ===== Custom Profile Header ===== */
.cp-header {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  padding: max(10px, env(safe-area-inset-top)) 20px 24px;
  color: #ffffff;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.15);
  position: relative;
  overflow: hidden;
}
.cp-header::after {
  content: "";
  position: absolute;
  top: -30px;
  right: -30px;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%);
  border-radius: 50%;
  pointer-events: none;
}
.cp-top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
}
.cp-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.cp-logout-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.2s;
}
.cp-logout-btn:active {
  background: rgba(255, 255, 255, 0.3);
}
.cp-user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  position: relative;
  z-index: 2;
}
.cp-avatar-wrap {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background-color: #94a3b8;
}
.cp-avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.cp-avatar-initial {
  font-size: 26px;
  font-weight: 800;
  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.cp-verified-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  background: #10b981;
  border: 2px solid #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.cp-name {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
}
.cp-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 13px;
  opacity: 0.9;
}
.cp-meta-label {
  font-weight: 500;
  opacity: 0.8;
  margin-right: 4px;
}
.cp-meta-value {
  font-weight: 700;
}
.cp-meta-value.mono {
  font-family: "SF Mono", Consolas, monospace;
}
.cp-meta-sep {
  opacity: 0.5;
}

/* ===== Stats ===== */
.p-stats {
  margin: 12px 16px 0;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
}
.p-stat-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 12px 8px;
  text-align: center;
  cursor: pointer;
  box-shadow: var(--c-card-shadow-soft);
  transition: transform 0.12s;
}
.p-stat-card:active {
  transform: scale(0.97);
}
.p-stat-card.readonly {
  cursor: default;
}
.p-stat-card.readonly:active {
  transform: none;
}
.p-stat-value {
  font-size: 14px;
  font-weight: 700;
  margin-top: 4px;
  color: var(--c-ink);
}
.p-stat-unit {
  font-size: 10px;
  color: var(--c-ink-soft);
}

/* ===== Section heads ===== */
.p-section-head {
  margin: 18px 16px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.p-section-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-ink-soft);
  display: flex;
  align-items: center;
  gap: 6px;
}
.p-section-action {
  background: transparent;
  border: none;
  font-size: 11px;
  color: var(--c-primary);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
}
.p-section-action:active {
  background: #eff6ff;
}

/* ===== List ===== */
.p-list {
  margin: 0 16px;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--c-card-shadow-soft);
}
.p-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  cursor: pointer;
}
.p-row.locked {
  cursor: not-allowed;
  opacity: 0.75;
}
.p-row.readonly {
  cursor: default;
}
.p-row-divider {
  height: 0.5px;
  background: #f0f2f7;
  margin: 0 14px;
}
.p-row-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.p-row-icon.blue {
  background: #e8f0fe;
  color: var(--c-primary);
}
.p-row-icon.amber {
  background: #fff4de;
  color: #c97a0c;
}
.p-row-icon.gray {
  background: #f0f2f7;
  color: var(--c-ink-soft);
}
.p-row-content {
  flex: 1;
  min-width: 0;
}
.p-row-label {
  font-size: 11px;
  color: var(--c-ink-soft);
}
.p-row-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--c-ink);
}
.p-row-value {
  font-size: 12px;
  color: var(--c-ink);
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.p-row-value.mono {
  font-family: "SF Mono", Consolas, monospace;
}
.p-row-value.multi {
  line-height: 1.5;
  display: block;
}
.p-row-sub {
  font-size: 11px;
  color: var(--c-ink-soft);
  margin-top: 2px;
}
.p-pill-verified {
  background: #eaf7f0;
  color: #1f7a4d;
  font-size: 9px;
  padding: 1px 6px;
  border-radius: 999px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.p-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px 0;
  font-size: 10px;
  color: var(--c-ink-soft);
  line-height: 1.5;
}

/* ===== Card block (emergency + line) ===== */
.p-card-block {
  margin: 0 16px;
  background: #ffffff;
  border-radius: 16px;
  padding: 14px;
  box-shadow: var(--c-card-shadow-soft);
}
.p-emerg {
  display: flex;
  align-items: center;
  gap: 12px;
}
.p-emerg-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #ffe8ec;
  color: #d83a5a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.p-emerg-info {
  flex: 1;
  min-width: 0;
}
.p-emerg-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-ink);
}
.p-emerg-rel {
  font-size: 11px;
  color: var(--c-ink-soft);
  margin-top: 1px;
}
.p-emerg-edit {
  cursor: pointer;
}

.p-line {
  display: flex;
  align-items: center;
  gap: 12px;
}
.p-line-icon {
  position: relative;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #eaf7f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.p-line-logo {
  background: #06c755;
  color: #ffffff;
  border-radius: 8px;
  padding: 4px 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
}
.p-line-dot {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #5bc78a;
  border: 2px solid #ffffff;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.p-line-info {
  flex: 1;
  min-width: 0;
}
.p-line-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-ink);
}
.p-line-sub {
  font-size: 11px;
  color: var(--c-ink-soft);
  margin-top: 1px;
}
.p-line-bonus {
  font-size: 10px;
  color: #1f7a4d;
  margin-top: 2px;
}
.p-line-btn {
  background: #ffffff;
  border: 1px solid var(--c-divider);
  color: var(--c-ink-soft);
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
}
.p-line-btn:active {
  background: #f1f5f9;
}

/* ===== Toggle card ===== */
.p-toggle-card {
  margin: 0 16px;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  padding: 4px 0;
  box-shadow: var(--c-card-shadow-soft);
}
.p-toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
}
.p-toggle-row.with-icon {
  gap: 12px;
}
.p-toggle-info {
  flex: 1;
  min-width: 0;
}
.p-toggle-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--c-ink);
}
.p-toggle-desc {
  font-size: 10px;
  color: var(--c-ink-soft);
  line-height: 1.5;
  margin-top: 2px;
}
.p-toggle {
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: #c5d2e8;
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  border: none;
  padding: 0;
  transition: background 0.2s;
}
.p-toggle.on {
  background: #5bc78a;
}
.p-toggle-knob {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  position: absolute;
  top: 2px;
  left: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: left 0.2s;
}
.p-toggle.on .p-toggle-knob {
  left: 18px;
}

/* ===== Action grid ===== */
.p-action-grid {
  margin: 10px 16px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.p-action-btn {
  background: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  font-family: inherit;
  box-shadow: var(--c-card-shadow-soft);
}
.p-action-btn span {
  font-size: 11px;
  font-weight: 600;
  color: var(--c-ink);
}
.p-action-btn:active {
  background: #f8fafc;
}

.p-logout {
  margin: 16px 16px 0;
  width: calc(100% - 32px);
  background: #ffffff;
  color: #d83a5a;
  border: 1px solid #ffe8ec;
  padding: 14px;
  border-radius: 14px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
}
.p-logout:active {
  background: #fff5f6;
}

.p-footer {
  text-align: center;
  font-size: 10px;
  color: #8b96b0;
  margin-top: 14px;
  line-height: 1.6;
  padding: 0 16px;
}

/* Phase 12.4 — consent history dialog */
.p-consent-loading {
  text-align: center;
  padding: 16px;
  color: var(--c-ink-soft);
}
.p-consent-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.p-consent-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 10px;
}
.p-consent-hosp {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-ink);
}
.p-consent-date {
  font-size: 11px;
  color: var(--c-ink-soft);
  margin-top: 2px;
}

/* ===== Current BMI strip ===== */
.p-bmi {
  width: 100%; margin-top: 10px; padding: 10px 14px;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px;
  font-family: inherit; cursor: pointer; text-align: left;
  transition: transform .15s ease, box-shadow .15s ease;
}
.p-bmi:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(15, 23, 42, .06); }
.p-bmi-left { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.p-bmi-label { font-size: 12px; color: #64748b; }
.p-bmi-value { font-size: 18px; font-weight: 700; color: #0f172a; }
.p-bmi-chip { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; }
.tone-ok .p-bmi-chip { background: #dcfce7; color: #15803d; }
.tone-warn .p-bmi-chip { background: #fef3c7; color: #b45309; }
.tone-bad .p-bmi-chip { background: #fee2e2; color: #b91c1c; }
.p-bmi-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; min-width: 96px; }
.p-bmi-gauge { position: relative; display: flex; width: 96px; height: 6px; border-radius: 3px; overflow: visible; }
.p-bmi-gauge .seg { flex: 1; height: 100%; }
.p-bmi-gauge .s1 { background: #fcd34d; border-radius: 3px 0 0 3px; flex: 0.175; }
.p-bmi-gauge .s2 { background: #4ade80; flex: 0.225; }
.p-bmi-gauge .s3 { background: #fbbf24; flex: 0.1; }
.p-bmi-gauge .s4 { background: #f87171; border-radius: 0 3px 3px 0; flex: 0.5; }
.p-bmi-dot {
  position: absolute; top: 50%; width: 12px; height: 12px; border-radius: 50%;
  background: #fff; border: 3px solid #0f172a; transform: translate(-50%, -50%);
}
.p-bmi-date { font-size: 10px; color: #94a3b8; }

/* Personal Info PIN Dialog & Readonly Box */
.p-pin-dialog :deep(.el-dialog__body) {
  padding: 16px 20px 24px;
}
.p-pin-dialog-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.p-pin-icon-wrap {
  width: 52px;
  height: 52px;
  background: #eff6ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}
.p-pin-hint {
  font-size: 13px;
  color: var(--c-ink-soft);
  margin: 0 0 16px;
  line-height: 1.4;
}
.p-pin-verifying {
  font-size: 12px;
  color: var(--c-primary);
  margin-top: 10px;
}
.p-readonly-box {
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px 14px;
  margin-top: 14px;
  border: 1px solid #f1f5f9;
}
.p-readonly-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 4px 0;
}
.p-readonly-label {
  color: var(--c-ink-soft);
}
.p-readonly-val {
  font-weight: 600;
  color: var(--c-ink);
}
.p-readonly-note {
  margin-top: 8px;
  font-size: 10px;
  color: var(--c-ink-soft);
  display: flex;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.4;
}
</style>
